import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { addClassNamesToElement } from "@lexical/utils";
import { headlessDecorate } from "./headless-decorate";
import { convertVideoElement, isVideoElement } from "./video/export";

import type { UUID } from "../../../../utils/uuid";
import type { SerializedDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import type {
  DOMConversionMap,
  DOMExportOutput,
  ElementFormatType,
  LexicalNode,
  NodeKey,
  Spread,
} from "lexical";

export type SerializedVideoNode = Spread<
  {
    version: 1;
    type: "video";
    videoUrl: string;
    thumbnailUrl: string;
    aspect: number;
    proportionOfMaxWidth: number;
    title?: string;
    sourceAssetId?: UUID;
    slackAppVideoUrl?: string;
  },
  SerializedDecoratorBlockNode
>;

export class VideoNode extends DecoratorBlockNode {
  __videoUrl: string;
  __thumbnailUrl: string;
  __aspect: number;
  __proportionOfMaxWidth: number;
  __title?: string; // Required for sending embedded Slack videos
  __sourceAssetId?: UUID;

  __slackAppVideoUrl?: string;

  constructor(
    videoUrl: string,
    thumbnailUrl: string,
    aspect: number,
    proportionOfMaxWidth: number,
    title?: string,
    sourceAssetId?: UUID,
    slackAppVideoUrl?: string,
    format?: ElementFormatType,
    key?: NodeKey,
  ) {
    super(format, key);
    this.__videoUrl = videoUrl;
    this.__thumbnailUrl = thumbnailUrl;
    this.__aspect = aspect;
    this.__proportionOfMaxWidth = proportionOfMaxWidth;
    this.__title = title;
    this.__sourceAssetId = sourceAssetId;
    this.__slackAppVideoUrl = slackAppVideoUrl;
  }

  static getType(): string {
    return "video";
  }

  static clone(node: VideoNode): VideoNode {
    return new this(
      node.__videoUrl,
      node.__thumbnailUrl,
      node.__aspect,
      node.__proportionOfMaxWidth,
      node.__title,
      node.__sourceAssetId,
      node.__slackAppVideoUrl,
      node.__format,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedVideoNode): VideoNode {
    return new this(
      serializedNode.videoUrl,
      serializedNode.thumbnailUrl,
      serializedNode.aspect,
      serializedNode.proportionOfMaxWidth,
      serializedNode.title,
      serializedNode.sourceAssetId,
      serializedNode.slackAppVideoUrl,
      serializedNode.format,
    );
  }

  exportJSON(): SerializedVideoNode {
    return {
      type: "video",
      version: 1,
      videoUrl: this.__videoUrl,
      thumbnailUrl: this.__thumbnailUrl,
      aspect: this.__aspect,
      proportionOfMaxWidth: this.__proportionOfMaxWidth,
      title: this.__title,
      sourceAssetId: this.__sourceAssetId,
      format: this.__format,
      slackAppVideoUrl: this.__slackAppVideoUrl,
    };
  }

  getHref(): string {
    return this.__videoUrl;
  }

  decorate(): React.JSX.Element {
    return headlessDecorate();
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("div");
    addClassNamesToElement(dom, "decorator-block-node");
    return dom;
  }

  getAspect(): number {
    return this.__aspect;
  }

  setSize(proportionOfMaxWidth: number, aspect: number): void {
    const writable = this.getWritable();
    writable.__proportionOfMaxWidth = proportionOfMaxWidth;
    writable.__aspect = aspect;
  }

  setNewUrl(thumbnailUrl: string): void {
    const writable = this.getWritable();
    writable.__thumbnailUrl = thumbnailUrl;
  }

  setTitle(title: string | undefined): void {
    const writable = this.getWritable();
    writable.__title = title;
  }

  setSlackAppVideoUrl(url: string): void {
    const writable = this.getWritable();
    writable.__slackAppVideoUrl = url;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("div");
    element.setAttribute("data-node-type", "video-node");
    element.setAttribute("node-video-url", this.__videoUrl);
    element.setAttribute("node-thumbnail-url", this.__thumbnailUrl);
    element.setAttribute("node-video-title", this.__title ?? "");
    element.setAttribute("node-video-asset-id", this.__sourceAssetId ?? "");
    element.style.width = `${(this.__proportionOfMaxWidth ?? 1) * 100}%`;
    element.style.aspectRatio = `${this.__aspect ?? 1}`;

    const link = document.createElement("a");
    link.href = this.__videoUrl;

    const img = document.createElement("img");
    img.src = this.__thumbnailUrl;

    link.appendChild(img);
    element.appendChild(link);

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (node: HTMLElement) => {
        if (!isVideoElement(node)) return null;

        return {
          conversion: (x) => convertVideoElement(x, VideoNode),
          priority: 1,
        };
      },
    };
  }
}

export function $isVideoNode(node: LexicalNode | null | undefined): node is VideoNode {
  return node instanceof VideoNode;
}
